import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConversationData,
  ConversationStatusGroup,
  RelativeTimeRange,
  WaConfigData,
} from './conversations-model';
import { RootState } from '../root/root-types';
import { ChannelDetails } from '../invoice';
import { MessageEnvelope } from '@zonofi/common/schema/message/inferface';

export const CONVERSATION_FETAURE_KEY = 'conversations';

export interface ConversationSliceState {
  conversationStatusMap: Record<ConversationStatusGroup, string[]>;
  currentConversation: ConversationData | null;
  channel: ChannelDetails;
  waConfig: WaConfigData[];
  messages: { [channel: string]: MessageEnvelope[] };
  msgPagination: { [channel: string]: boolean };
  scrollToBottom: boolean;
  selectedTimeRange: RelativeTimeRange;
}

export const initialConversationDataState: ConversationSliceState = {
  conversationStatusMap: {
    [ConversationStatusGroup.OPEN]: [],
    [ConversationStatusGroup.INPROGRESS]: [],
    [ConversationStatusGroup.CLOSED]: [],
  },
  currentConversation: null,
  channel: {} as ChannelDetails,
  waConfig: [],
  messages: {},
  msgPagination: {},
  scrollToBottom: false,
  selectedTimeRange: 'LAST_24_HOURS',
};

export const conversationSlice = createSlice({
  name: CONVERSATION_FETAURE_KEY,
  initialState: initialConversationDataState,
  reducers: {
    loadConversationStatusMap(
      state,
      action: PayloadAction<Record<ConversationStatusGroup, string[]>>
    ) {
      state.conversationStatusMap = action.payload;
    },
    updateCurrentConversation(
      state,
      action: PayloadAction<ConversationData | null>
    ) {
      state.currentConversation = action.payload;
    },
    updateChannel(state, action: PayloadAction<ChannelDetails>) {
      state.channel = action.payload;
    },
    updateWaConfig(state, action: PayloadAction<WaConfigData[]>) {
      state.waConfig = action.payload;
    },
    updateMessagesByChannelId(
      state,
      action: PayloadAction<{ channelId: string; messages: MessageEnvelope[] }>
    ) {
      const { channelId, messages } = action.payload;
      if (!state.messages) {
        state.messages = {};
      }
      const existingMessages = state.messages[channelId] ?? [];
      const mergedMessages = new Map<string, MessageEnvelope>();

      const upsertMergedMessage = (message: MessageEnvelope) => {
        const messageKey =
          message.message?._id ??
          message.timetoken?.toString() ??
          String(message.displayTimetoken ?? '');
        const previousMessage = mergedMessages.get(messageKey);

        if (!previousMessage) {
          mergedMessages.set(messageKey, message);
          return;
        }

        mergedMessages.set(messageKey, {
          ...previousMessage,
          ...message,
          message: {
            ...previousMessage.message,
            ...message.message,
          },
        });
      };

      existingMessages.forEach(upsertMergedMessage);
      messages.forEach(upsertMergedMessage);

      state.messages[channelId] = Array.from(mergedMessages.values()).sort(
        (a, b) =>
          Number(a.displayTimetoken ?? a.timetoken) -
          Number(b.displayTimetoken ?? b.timetoken)
      );
    },
    updateChannelPagination(
      state,
      action: PayloadAction<{ channelId: string; pagination: boolean }>
    ) {
      const { channelId, pagination } = action.payload;
      if (!state.msgPagination) {
        state.msgPagination = {};
      }

      state.msgPagination[channelId] = pagination;
    },
    clearAllMessageData(state) {
      state.messages = {};
      state.msgPagination = {};
    },
    updateScrollToBottom(state, action: PayloadAction<boolean>) {
      state.scrollToBottom = action.payload;
    },
    updateSelectedTimeRange: (
      state,
      action: PayloadAction<RelativeTimeRange>
    ) => {
      state.selectedTimeRange = action.payload;
    },
    clearSelectedTimeRange: (state) => {
      state.selectedTimeRange = 'LAST_24_HOURS';
    },
  },
});

export const {
  loadConversationStatusMap,
  updateCurrentConversation,
  updateChannel,
  updateWaConfig,
  updateMessagesByChannelId,
  updateChannelPagination,
  clearAllMessageData,
  updateScrollToBottom,
  updateSelectedTimeRange,
  clearSelectedTimeRange,
} = conversationSlice.actions;

export const getConversationSliceState = (
  rootState: RootState
): ConversationSliceState => rootState[CONVERSATION_FETAURE_KEY];

export const getConversationStatusMap = createSelector(
  getConversationSliceState,
  (state) => state.conversationStatusMap
);

export const getCurrentConversation = createSelector(
  getConversationSliceState,
  (state) => state.currentConversation
);

export const getChannel = createSelector(
  getConversationSliceState,
  (state) => state.channel
);

export const getWaConfig = createSelector(getConversationSliceState, (state) =>
  state.waConfig?.find((x) => x.isDefault)
);

export const getWaConfigId = createSelector(
  getConversationSliceState,
  (state) => state.waConfig?.find((x) => x.isDefault)?.id
);

export const getWahaConfigId = createSelector(
  getConversationSliceState,
  (state) => state.waConfig.find((x) => x.provider === 'WAHA')?.id
);

export const getChannelMessages = (channelId: string) =>
  createSelector(
    getConversationSliceState,
    (state) => state.messages?.[channelId] || []
  );

export const getAllMessages = createSelector(
  getConversationSliceState,
  (state) => state.messages
);

export const getCurrentChannelPagination = (channelId: string) =>
  createSelector(
    getConversationSliceState,
    (state) => state.msgPagination?.[channelId] || false
  );

export const getIsScrollToBottom = createSelector(
  getConversationSliceState,
  (state) => state.scrollToBottom
);

export const getSelectedTimeRange = createSelector(
  getConversationSliceState,
  (state) => state.selectedTimeRange
);
